import { Link, useNavigate } from "react-router-dom";
import { Paper, Title, Text, Loader, Center, Button, Container, Divider, Group, ScrollArea, Space, Stack, ThemeIcon, Menu, SimpleGrid, Progress, useMantineTheme, ActionIcon, Tabs, Badge } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconPencil, IconDots, IconTrash, IconUser, IconRotate2, IconChevronLeft, IconUsers } from "@tabler/icons-react";
import moment from "moment";

import { ListWorkOrderDto } from "../../models/workorder.models";
import { UserDto } from "../../models/user.models";
import { UserWorkOrderList } from "./UserWorkOrderList";
import { UserEventList } from "./UserEventList";
import { useAccess } from "../../Auth/auth-functions";
import { AzureADRoles } from "../../Auth/auth-roles";
import { CrudUserDate } from "../Shared/CrudUserDate";
import classes from './UserDetailView.module.css';

interface UserDetailViewProps {
	user?: UserDto | null;
	isLoading?: boolean;
	onEdit?: () => void;
	onDelete?: (user: UserDto) => void;
	onRestore?: (user?: UserDto) => void;
	isRestoring?: boolean;
	onClose?: () => void;
}

export const UserDetailView = ({ user, isLoading, onEdit, onDelete, onRestore, isRestoring, onClose }: UserDetailViewProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const canEditUser = useAccess(AzureADRoles.Admin);

	const percentageCompleted = user && user.WorkOrdersAssigned > 0 ? Math.round(user.WorkOrdersCompleted / user.WorkOrdersAssigned * 100) : 0;

	const handleWorkOrderSelected = (workOrder: ListWorkOrderDto): void => {
		navigate(`/workorders/${workOrder.WorkOrderId}`);
	};

	const deleteUser = () => {
		if (!user) return;

		if (onDelete && user)
			onDelete(user);
	}

	const restoreUser = () => {
		if (!user) return;

		if (onRestore && user)
			onRestore(user);
	}

	if (isLoading) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>

	if (!user) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Space h="md" />
				<Stack justify="center" align="center" gap="md" pt="lg">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconUser size={60} />
					</ThemeIcon>
					<Text fz="lg" fw="500" c="dimmed">Select a user to view details</Text>
				</Stack>
				<Space h="md" />
			</ScrollArea>
		</Paper>

	return <>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
								<IconChevronLeft />
							</ActionIcon>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3}>{user?.DisplayName}</Title>
								{!user.Deleted &&
								<Text fz="xs" c="dimmed">Created on {moment(user?.Created).format("MMMM D, YYYY")}</Text>
								}
								{user.Deleted &&
								<Text fz="xs" c="dimmed">Deleted on {moment(user?.Deleted).format("MMMM D, YYYY")}</Text>
								}
							</Stack>
						</Group>
						<Group gap="xs">
							<Button.Group mr={-10}>
								{user.Deleted == null && <>
									{!isMobile &&
									<Button onClick={onEdit} disabled={!canEditUser} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
									}
									{isMobile &&
									<Button onClick={onEdit} disabled={!canEditUser} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
									}
									<Divider orientation="vertical" />
									<Menu position="bottom-end" shadow="md" radius="lg">
										<Menu.Target>
											<Button disabled={!canEditUser} variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
										</Menu.Target>
										<Menu.Dropdown>
											{/* <Menu.Item onClick={previewWorkOrder} leftSection={<IconFileSearch stroke={1.5} />}>Preview PDF</Menu.Item>
											<Menu.Item onClick={printWorkOrder} leftSection={<IconPrinter stroke={1.5} />}>Print PDF</Menu.Item>
											<Menu.Item onClick={downloadWorkOrder} leftSection={<IconFileDownload stroke={1.5} />}>Download PDF</Menu.Item>
											<Menu.Item onClick={openEmailModal} leftSection={<IconMailForward stroke={1.5} />}>Email PDF</Menu.Item>
											<Menu.Divider />
											<Menu.Item onClick={copyWorkOrder} leftSection={<IconCopy stroke={1.5} />}>Copy to new Work Order</Menu.Item>
											<Menu.Divider />*/}
											<Menu.Item onClick={deleteUser} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
										</Menu.Dropdown>
									</Menu>
								</>}
								{user.Deleted != null && (
									<Button onClick={restoreUser} loading={isRestoring} variant="outline" radius="lg" leftSection={<IconRotate2 stroke={1.5} />}>Restore</Button>
								)}
							</Button.Group>
						</Group>
					</Group>
				</Stack>

				{/* <Space h="md" />
				<Group justify="space-between" px="20">
					<Title order={3}>{location?.Title}</Title>
					<Button variant="outline" radius="md" leftSection={<IconPencil />} onClick={onEdit}>Edit</Button>
				</Group>
				<Space h="md" />
				<Divider mb="md" /> */}

				{user.Deleted && <>
					<Container px="20" mt={40}>
						<Stack align="center">
							<ThemeIcon size={90} radius={60} variant="light" color="gray">
								<IconTrash size={60} />
							</ThemeIcon>
							<Stack align="center" gap={0}>
								<Text fz="xl">User Deleted</Text>
								<Text fz="xs" c="dimmed">Deleted on {moment(user?.Deleted).format("MMMM D, YYYY")}</Text>
							</Stack>
						</Stack>
					</Container>
				</>}

				<Container px="20" hidden={user.Deleted != null}>
					{/* <Space h="md" /> */}
					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Role</Text>
					<Space h="xs" />
					<Text fz="sm">{user?.Role}</Text>
					<Space h="md" />

					{user?.Email && <>
					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Email</Text>
					<Space h="xs" />
					<Text fz="sm">{user?.Email}</Text>
					<Space h="md" />
					</>}

					{user?.PhoneNumber && <>
					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Phone Number</Text>
					<Space h="xs" />
					<Text fz="sm" pb="md">{user?.PhoneNumber}</Text>
					</>}
					
					<SimpleGrid cols={2}>

					{true && <Stack gap="xs" pb="md">
					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Work Orders Assigned</Text>
					<Text fz="sm">{user?.WorkOrdersAssigned}</Text>
					</Stack>}
					
					{true && <Stack gap="xs">
					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Percentage Completed</Text>
					<Progress value={percentageCompleted} size="md" mt="xs" radius="xl" />
					</Stack>}
					</SimpleGrid>

					{user?.Teams.length > 0 && <>
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Teams</Text>
						<Space h="xs" />
						<Group wrap="wrap" gap="xs">
							{user.Teams.map(team => (
								<Link key={team.TeamId} to={{ pathname: `/teams/${team.TeamId}` }} className={classes['link']}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconUsers size={14} />}>{team.Name}</Badge>
								</Link>
							))}
						</Group>
						<Divider my="md" />
					</>}

					<SimpleGrid cols={isMobile ? 1 : 2}>
						<CrudUserDate label="Created" userId={user.CreatedBy.UserId} userName={user.CreatedBy.Name} date={user.Created} />
						{user.UpdatedBy && !user.Deleted && <>
							<CrudUserDate label="Updated" userId={user.UpdatedBy.UserId} userName={user.UpdatedBy.Name} date={user.Updated} />
						</>}
						{user.DeletedBy && <>
							<CrudUserDate label="Deleted" userId={user.DeletedBy.UserId} userName={user.DeletedBy.Name} date={user.Deleted} />
						</>}
					</SimpleGrid>
					<Divider my="md" />

					<Space h="md" />
					<Tabs defaultValue="workorders" radius="md" className={classes.tabs}>
						<Tabs.List grow>
							<Tabs.Tab value="workorders">Work Order History</Tabs.Tab>
							<Tabs.Tab value="recent-activity">Recent Activity</Tabs.Tab>
							<Tabs.Tab value="recent-comments">Recent Comments</Tabs.Tab>
						</Tabs.List>
						<Tabs.Panel value="workorders">
							<Space h="md" />
							<UserWorkOrderList userId={user.UserId} onSelect={handleWorkOrderSelected} />
						</Tabs.Panel>
						<Tabs.Panel value="recent-activity">
							<Space h="xl" />
							<UserEventList userId={user.UserId} comments={false} />
						</Tabs.Panel>							
						<Tabs.Panel value="recent-comments">
							<Space h="xl" />
							<UserEventList userId={user.UserId} comments={true} />
						</Tabs.Panel>							
					</Tabs>
					<Space h="md" />
					
				</Container>
			</ScrollArea>
		</Paper>
	</>;
}
